/**
 * Domain configuration for multi-domain deployment support.
 *
 * This module enables the ecommerce app to deploy to both tryfi.com and
 * fitracking.com domains with configurable subdomain URLs. The configuration
 * is determined at build time via environment variables and cached for
 * runtime performance.
 *
 * Environment variables:
 * - REACT_APP_BASE_DOMAIN: Base domain (defaults to 'tryfi.com')
 * - REACT_APP_HIDE_FROM_ROBOTS: Set to 'true' to block search engine indexing
 *   (adds noindex meta tag and disallows crawlers in robots.txt)
 */

export interface HreflangEntry {
  hreflang: string;
  shopDomain: string;
}

export interface DomainConfig {
  baseDomain: string;
  shopDomain: string;
  nanoDomain: string;
  blogDomain: string;
  supportDomain: string;
  membershipsDomain: string;
  cookieDomain: string;
  hideFromRobots: boolean;
  /**
   * hreflang mappings for SEO during the tryfi.com → fitracking.com migration.
   * Tells search engines which domain is the regional variant for each locale,
   * preventing duplicate content issues while both domains serve geo-adaptive content.
   */
  hreflangEntries: HreflangEntry[];
  /** The hreflang value for the x-default fallback domain. */
  xDefaultShopDomain: string;
}

let cachedConfig: DomainConfig | null = null;

export function getDomainConfig(): DomainConfig {
  if (cachedConfig) {
    return cachedConfig;
  }

  const baseDomain = process.env.REACT_APP_BASE_DOMAIN || 'tryfi.com';
  const hideFromRobots = process.env.REACT_APP_HIDE_FROM_ROBOTS === 'true';

  cachedConfig = {
    baseDomain,
    shopDomain: `shop.${baseDomain}`,
    nanoDomain: `nano.${baseDomain}`,
    blogDomain: `blog.${baseDomain}`,
    supportDomain: `support.${baseDomain}`,
    membershipsDomain: `memberships.${baseDomain}`,
    cookieDomain: `.${baseDomain}`,
    hideFromRobots,
    hreflangEntries: [
      { hreflang: 'en-us', shopDomain: 'shop.fitracking.com' },
      { hreflang: 'en-ca', shopDomain: 'shop.fitracking.com' },
      { hreflang: 'fr-ca', shopDomain: 'shop.fitracking.com' },
      { hreflang: 'en-gb', shopDomain: 'shop.fitracking.com' },
    ],
    xDefaultShopDomain: 'shop.fitracking.com',
  };

  return cachedConfig;
}
